import React from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';

import { Form, FieldTextInput, IconSpinner } from '../../../components';

import { IconMail } from '../WaitlistIcons';

import css from './WaitlistForm.module.css';

// Error code returned by server/api/waitlist.js when Brevo rejects the email address
const ERROR_INVALID_EMAIL = 'invalid-email';

/**
 * Email signup form for the waitlist. Shows a success message in place of the
 * form after a successful submission.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.formId unique id for the form (used for input ids)
 * @param {ReactNode} props.submitButtonText text of the submit button
 * @param {Function} props.onSubmit called with form values, must return a Promise
 * @returns {JSX.Element} waitlist form
 */
const WaitlistForm = props => {
  const intl = useIntl();
  const { rootClassName, className, formId, submitButtonText, onSubmit } = props;

  const submitWaitlist = values =>
    onSubmit(values)
      .then(() => undefined)
      .catch(e => ({ [FORM_ERROR]: e?.code === ERROR_INVALID_EMAIL ? 'invalid' : 'generic' }));

  return (
    <FinalForm
      onSubmit={submitWaitlist}
      render={formRenderProps => {
        const { handleSubmit, submitting, submitSucceeded, submitError } = formRenderProps;
        const classes = classNames(rootClassName || css.root, className);

        if (submitSucceeded) {
          return (
            <div className={classNames(classes, css.success)} role="status">
              <FormattedMessage id="WaitlistForm.success" />
            </div>
          );
        }

        const emailLabel = intl.formatMessage({ id: 'WaitlistForm.emailLabel' });
        const emailPlaceholder = intl.formatMessage({ id: 'WaitlistForm.emailPlaceholder' });
        const emailRequired = validators.required(
          intl.formatMessage({ id: 'WaitlistForm.emailRequired' })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({ id: 'WaitlistForm.emailInvalid' })
        );

        const submitErrorMessage =
          submitError === 'invalid' ? (
            <FormattedMessage id="WaitlistForm.emailInvalid" />
          ) : submitError ? (
            <FormattedMessage id="WaitlistForm.genericError" />
          ) : null;

        return (
          // noValidate: show the form's own validation messages instead of browser popups
          <Form className={classes} onSubmit={handleSubmit} noValidate>
            <div className={css.emailField}>
              <IconMail className={css.emailIcon} />
              <FieldTextInput
                type="email"
                id={`${formId}.email`}
                name="email"
                autoComplete="email"
                label={emailLabel}
                labelClassName={css.visuallyHidden}
                inputRootClass={css.emailInput}
                placeholder={emailPlaceholder}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
            </div>

            {/* Honeypot for bots: hidden from people and assistive technologies */}
            <div className={css.honeypot} aria-hidden="true">
              <Field
                name="company"
                component="input"
                type="text"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {submitErrorMessage ? (
              <p className={css.submitError} role="alert">
                {submitErrorMessage}
              </p>
            ) : null}

            <button className={css.submitButton} type="submit" disabled={submitting}>
              {submitting ? <IconSpinner rootClassName={css.spinner} /> : submitButtonText}
            </button>
          </Form>
        );
      }}
    />
  );
};

export default WaitlistForm;
